/*
 * Checkout Flow — noyau JavaScript partagé (module, chargé une fois par page).
 * Expose window.CF pour les modules de section. Amélioration progressive : sans ce fichier,
 * tout le contenu reste lisible et les liens / formulaires fonctionnent.
 */
(() => {
  if (window.CF) return;

  const root = document.documentElement;
  const config = window.CF_CONFIG || {};
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const smooth = (t) => { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); };
  const lerp = (a, b, t) => a + (b - a) * t;
  const seg = (v, a, b) => smooth((v - a) / (b - a));

  /* ---------- préférence de mouvement : 'full' | 'soft' | 'off' ---------- */
  const reducedQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function motion() {
    if (reducedQuery && reducedQuery.matches) return 'off';
    return root.getAttribute('data-cf-motion') || 'full';
  }
  if (reducedQuery && reducedQuery.addEventListener) {
    reducedQuery.addEventListener('change', () => {
      let stored = null;
      try { stored = localStorage.getItem('cf-motion'); } catch (e) { /* noop */ }
      root.setAttribute('data-cf-motion', reducedQuery.matches || stored === 'off' ? 'off' : (config.motion || 'full'));
      syncMotionToggles();
      document.dispatchEvent(new CustomEvent('cf:motion-change'));
    });
  }

  /* ---------- conteneur de défilement (Horizon fait défiler .page-wrapper sur desktop) ---------- */
  function scroller() {
    const pw = document.querySelector('.page-wrapper');
    if (pw) {
      const cs = getComputedStyle(pw);
      if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') && pw.scrollHeight > pw.clientHeight + 1) return pw;
    }
    return document.scrollingElement || document.documentElement;
  }
  const scrollTop = () => scroller().scrollTop;

  /* ---------- boucle d'animation unique, suspendue quand l'onglet est masqué ---------- */
  const tasks = new Set();
  let rafId = 0;
  let lastT = 0;
  function frame(t) {
    rafId = 0;
    if (document.hidden) { lastT = 0; return; }
    loop.dt = lastT ? Math.min(50, Math.max(1, t - lastT)) : 16.667;
    lastT = t;
    tasks.forEach((fn) => { try { fn(t); } catch (e) { tasks.delete(fn); console.error(e); } });
    if (tasks.size) rafId = requestAnimationFrame(frame);
    else lastT = 0;
  }
  const loop = {
    dt: 16.667,
    add(fn) { tasks.add(fn); if (!rafId && !document.hidden) rafId = requestAnimationFrame(frame); },
    remove(fn) { tasks.delete(fn); },
    /* lissage indépendant de la fréquence d'images : même vitesse à 30, 60 ou 120 images/s */
    k(rate, dt) { return 1 - Math.pow(1 - rate, (dt || loop.dt) / 16.667); },
  };
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && tasks.size && !rafId) rafId = requestAnimationFrame(frame);
  });

  /* ---------- défilement : écoute en capture (fonctionne quel que soit le conteneur) ---------- */
  const scrollSubs = new Set();
  let scrollQueued = false;
  function onScrollEvent() {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(() => { scrollQueued = false; scrollSubs.forEach((fn) => fn()); });
  }
  document.addEventListener('scroll', onScrollEvent, { capture: true, passive: true });
  window.addEventListener('resize', onScrollEvent, { passive: true });
  const onScroll = (fn) => { scrollSubs.add(fn); return () => scrollSubs.delete(fn); };

  /* ---------- visibilité ---------- */
  /* Initialisation différée : fn() s'exécute quand l'élément approche de l'écran (hors du chargement initial).
     Réservé aux scènes décoratives dont l'initialisation ne change pas la mise en page. */
  function whenNear(el, fn, margin = 900) {
    if (!('IntersectionObserver' in window) || config.designMode || (window.Shopify && window.Shopify.designMode)) { fn(); return () => {}; }
    const sc = scroller();
    const root = sc === document.scrollingElement || sc === document.documentElement ? null : sc;
    let done = false;
    const io = new IntersectionObserver((entries) => {
      if (done || !entries.some((e) => e.isIntersecting)) return;
      done = true; io.disconnect(); fn();
    }, { root, rootMargin: `${margin}px 0px` });
    io.observe(el);
    return () => { done = true; io.disconnect(); };
  }

  function observeVisible(el, cb, rootMargin = '120px 0px') {
    if (!('IntersectionObserver' in window)) { cb(true); return () => {}; }
    const io = new IntersectionObserver((entries) => entries.forEach((e) => cb(e.isIntersecting, e)), { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }

  /* ---------- mesure légère (aucune donnée personnelle, jamais dans l'éditeur) ---------- */
  const trackedOnce = new Set();
  function track(name, data = {}, once = false) {
    if (!config.analytics || config.designMode || (window.Shopify && window.Shopify.designMode)) return;
    const key = name + JSON.stringify(data);
    if (once && trackedOnce.has(key)) return;
    trackedOnce.add(key);
    const payload = Object.assign({ locale: config.locale || root.lang || '' }, data);
    try { if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) window.Shopify.analytics.publish('cf_' + name, payload); } catch (e) { /* noop */ }
    document.dispatchEvent(new CustomEvent('cf:track', { detail: { name, data: payload } }));
  }

  /* ---------- préremplissage du devis (service, détail, pays, configurateur) ----------
     Stocké en sessionStorage uniquement, sans coordonnées personnelles. */
  const QKEY = 'cf-quote-prefill';
  const quote = {
    get() { try { return JSON.parse(sessionStorage.getItem(QKEY) || '{}'); } catch (e) { return {}; } },
    set(patch) {
      const next = Object.assign(quote.get(), patch);
      Object.keys(next).forEach((k) => { if (next[k] === '' || next[k] == null) delete next[k]; });
      try { sessionStorage.setItem(QKEY, JSON.stringify(next)); } catch (e) { /* stockage indisponible */ }
      document.dispatchEvent(new CustomEvent('cf:quote-prefill', { detail: next }));
      return next;
    },
    clear() { try { sessionStorage.removeItem(QKEY); } catch (e) { /* noop */ } },
  };

  function findQuoteForm() { return document.querySelector('cf-quote-form'); }

  function goToQuote(link, patch) {
    quote.set(patch);
    const form = findQuoteForm();
    if (form) {
      const target = document.getElementById('quote') || form;
      target.scrollIntoView({ behavior: motion() === 'off' ? 'auto' : 'smooth', block: 'start' });
      if (form.focusStart) setTimeout(() => form.focusStart(), motion() === 'off' ? 0 : 650);
      return true;
    }
    return false;
  }

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cf-quote]');
    if (link) {
      const patch = {};
      const service = link.getAttribute('data-cf-quote');
      if (service) patch.service = service;
      patch.detail = link.getAttribute('data-cf-quote-detail') || '';
      const country = link.getAttribute('data-cf-quote-country');
      if (country) patch.country = country;
      const conf = link.getAttribute('data-cf-quote-config');
      if (conf) patch.config = conf;
      track('quote_cta_click', { location: link.getAttribute('data-cf-track') || '', service: service || '' });
      if (goToQuote(link, patch)) e.preventDefault();
      return;
    }
    /* ancre absente de la page : on renvoie vers la même ancre sur l'accueil */
    const a = e.target.closest('a[href^="#"]');
    if (a && a.getAttribute('href').length > 1) {
      const id = decodeURIComponent(a.getAttribute('href').slice(1));
      if (!document.getElementById(id)) {
        e.preventDefault();
        window.location.href = (config.rootUrl || '/') + '#' + id;
      }
    }
  });

  /* ---------- apparitions au défilement ---------- */
  let revealIO = null;
  function initReveal(scope = document) {
    const els = scope.querySelectorAll('.cf-reveal:not(.is-in)');
    if (!els.length) return;
    if (motion() === 'off' || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
    if (!revealIO) {
      revealIO = new IntersectionObserver((entries) => entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); revealIO.unobserve(en.target); }
      }), { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    }
    els.forEach((el) => revealIO.observe(el));
  }

  /* ---------- disclosures (langue, menu) : fermeture au clic extérieur et à Échap ---------- */
  document.addEventListener('click', (e) => {
    document.querySelectorAll('details[data-cf-disclosure][open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('details[data-cf-disclosure][open]').forEach((d) => {
      d.removeAttribute('open');
      const s = d.querySelector('summary');
      if (s && d.contains(document.activeElement)) s.focus();
    });
  });

  /* ---------- bouton « mettre les animations en pause » (préférence du visiteur, locale) ---------- */
  function syncMotionToggles() {
    const off = root.getAttribute('data-cf-motion') === 'off';
    document.querySelectorAll('[data-cf-motion-toggle]').forEach((b) => b.setAttribute('aria-pressed', String(off)));
  }
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-cf-motion-toggle]');
    if (!b) return;
    const off = root.getAttribute('data-cf-motion') !== 'off';
    if (!off && reducedQuery && reducedQuery.matches) return; // le réglage de l'appareil reste prioritaire
    root.setAttribute('data-cf-motion', off ? 'off' : (config.motion || 'full'));
    try { if (off) localStorage.setItem('cf-motion', 'off'); else localStorage.removeItem('cf-motion'); } catch (err) { /* noop */ }
    syncMotionToggles();
    document.dispatchEvent(new CustomEvent('cf:motion-change'));
    track('motion_toggle', { state: off ? 'off' : 'on' });
  });

  /* ---------- compteurs (valeur finale déjà rendue côté serveur) ---------- */
  function initCounters(scope = document) {
    const els = scope.querySelectorAll('[data-cf-count]:not([data-cf-counted])');
    els.forEach((el) => {
      el.setAttribute('data-cf-counted', '');
      if (motion() === 'off') return;
      const to = parseInt(el.getAttribute('data-cf-count'), 10);
      if (!to) return;
      const stop = observeVisible(el, (vis) => {
        if (!vis) return;
        stop();
        const t0 = performance.now();
        const tick = (now) => {
          const k = clamp((now - t0) / 1300, 0, 1);
          el.textContent = String(Math.round(to * smooth(k)));
          if (k < 1) requestAnimationFrame(tick);
        };
        el.textContent = '0';
        requestAnimationFrame(tick);
      }, '0px');
    });
  }

  /* ==========================================================================
     Cycle de vie des éléments : déplacer une section dans l'éditeur de thème
     déconnecte puis reconnecte immédiatement l'élément. Le nettoyage est donc
     différé et annulé si l'élément est toujours dans le document ; les écouteurs
     posés sur ses propres enfants ne sont posés qu'une fois (pas de doublons).
     ========================================================================== */
  const live = (el) => { if (el._cfLive) return false; el._cfLive = true; return true; };
  const firstBind = (el) => { if (el._cfBound) return false; el._cfBound = true; return true; };
  const release = (el, fn) => queueMicrotask(() => { if (el.isConnected || !el._cfLive) return; el._cfLive = false; fn(); });

  /* ==========================================================================
     <cf-header> : ombre au défilement, masquage au défilement vers le bas, menu mobile
     ========================================================================== */
  /* séquences épinglées (mobile) : le contenu remonte quand l'en-tête se masque. La classe n'est posée que sur
     les cadres visibles (pas sur <html>, pas sur les cadres hors écran) pour éviter des recalculs de style inutiles. */
  let hdrOff = false;
  const hdrFrames = new Set();
  const hdrIO = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => entries.forEach((en) => {
    if (en.isIntersecting) { hdrFrames.add(en.target); en.target.classList.toggle('is-hdr-off', hdrOff); } else hdrFrames.delete(en.target);
  })) : null;
  function watchHdrFrames(scope = document) {
    if (!hdrIO) return;
    scope.querySelectorAll('.cf-exp__frame, .cf-int--seq cf-integration').forEach((el) => { if (!el._cfHdr) { el._cfHdr = true; hdrIO.observe(el); } });
  }
  function setHdrOff(v) {
    if (v === hdrOff) return;
    hdrOff = v;
    hdrFrames.forEach((el) => el.classList.toggle('is-hdr-off', v));
  }

  class CFHeader extends HTMLElement {
    connectedCallback() {
      if (!live(this)) return;
      this.bar = this.querySelector('.cf-header');
      this.menu = this.querySelector('.cf-menu');
      this.last = scrollTop();
      this.unsub = onScroll(() => this.update());
      this.update();
      if (this.menu && firstBind(this)) {
        this.menu.addEventListener('toggle', () => {
          this.bar.classList.toggle('is-menu-open', this.menu.open);
          if (this.menu.open) track('menu_open', {}, true);
        });
        this.menu.addEventListener('click', (e) => { if (e.target.closest('a')) this.menu.removeAttribute('open'); });
      }
    }
    disconnectedCallback() { release(this, () => { if (this.unsub) this.unsub(); }); }
    update() {
      const y = scrollTop();
      this.bar.classList.toggle('is-solid', y > 24);
      const hide = this.hasAttribute('data-hide-on-scroll') && y > this.last && y > 320 && !(this.menu && this.menu.open);
      this.bar.classList.toggle('is-hidden', hide);
      setHdrOff(hide);
      this.last = y;
    }
  }
  if (!customElements.get('cf-header')) customElements.define('cf-header', CFHeader);

  /* ==========================================================================
     <cf-mobile-cta> : visible après le premier écran, masqué près du formulaire,
     quand un autre bouton de devis est visible, quand le clavier est ouvert
     ou quand la bannière de consentement Shopify est affichée.
     ========================================================================== */
  class CFMobileCta extends HTMLElement {
    connectedCallback() {
      if (!live(this)) return;
      this.visibleCtas = new Set();
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) this.visibleCtas.add(en.target); else this.visibleCtas.delete(en.target); });
        this.update();
      }, { threshold: 0.01 });
      this.watch();
      this.unsub = onScroll(() => this.update());
      this.onFocus = () => this.update();
      document.addEventListener('focusin', this.onFocus);
      document.addEventListener('focusout', this.onFocus);
      this.mo = new MutationObserver(() => this.update());
      this.mo.observe(document.body, { childList: true });
      this.update();
    }
    watch() {
      document.querySelectorAll('[data-cf-quote], .cf-quote').forEach((el) => {
        if (!this.contains(el) && !el.closest('cf-header')) this.io.observe(el);
      });
    }
    disconnectedCallback() {
      release(this, () => {
        this.io.disconnect(); this.mo.disconnect(); if (this.unsub) this.unsub();
        document.removeEventListener('focusin', this.onFocus); document.removeEventListener('focusout', this.onFocus);
      });
    }
    consentVisible() {
      const b = document.getElementById('shopify-pc__banner');
      return !!(b && b.offsetParent !== null && getComputedStyle(b).visibility !== 'hidden');
    }
    update() {
      const a = document.activeElement;
      const typing = a && (a.matches('input, textarea, select') || a.isContentEditable);
      const past = scrollTop() > window.innerHeight * 0.8;
      const otherCta = this.visibleCtas.size > 0;
      const header = document.querySelector('cf-header .cf-header');
      const headerCta = header && !header.classList.contains('is-hidden') && header.querySelector('.cf-header__quote');
      const headerShowsCta = !!(headerCta && headerCta.offsetParent !== null);
      const show = past && !otherCta && !headerShowsCta && !typing && !this.consentVisible();
      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
    }
  }
  if (!customElements.get('cf-mobile-cta')) customElements.define('cf-mobile-cta', CFMobileCta);

  /* ---------- accordéons progressifs (configurateur, formulaire de devis) ----------
     Chaque question devient un en-tête (titre + choix résumé + « Modifier ») ; une seule question ouverte à la fois.
     Après un choix (souris ou toucher), la question se replie et la suivante sans réponse s'ouvre. Au clavier, les flèches
     changent le choix sans replier ; la question se replie quand le focus la quitte. Aucune valeur n'est modifiée :
     les champs restent dans le formulaire (préremplissage, envoi Shopify et validation inchangés). Sans JavaScript : rien ne change. */
  let accUid = 0;
  function accordion(root, sel, opts = {}) {
    if (!root || root._cfAcc) return root && root._cfAcc;
    const editTxt = opts.edit || 'Edit';
    const visible = (q) => !q.hidden && !q.closest('[hidden]');
    const all = () => [...root.querySelectorAll(sel)];
    const titleEl = (q) => q.querySelector(':scope > legend, :scope > .cf-field__label, :scope > .cf-conf__label');
    const valueOf = (q) => {
      const checked = [...q.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked')];
      if (checked.length) return checked.map((i) => { const sp = i.closest('label') && i.closest('label').querySelector('span'); return (sp ? sp.textContent : i.value).trim(); }).join(', ');
      const sl = q.querySelector('select');
      if (sl && sl.value && !/^(prov_none)$/.test(sl.value)) return sl.options[sl.selectedIndex].text.trim();
      return '';
    };
    const api = {
      setup(q) {
        if (q._acc) return;
        const t = titleEl(q);
        if (!t) return;
        q._acc = true;
        q.classList.add('cf-acc');
        const id = 'cf-acc-' + (++accUid);
        const body = document.createElement('div');
        body.className = 'cf-acc__body';
        body.id = id;
        [...q.childNodes].forEach((n) => { if (n !== t) body.appendChild(n); });
        const head = document.createElement('button');
        head.type = 'button';
        head.className = 'cf-acc__head';
        head.setAttribute('aria-controls', id);
        head.innerHTML = '<span class="cf-acc__q"></span><span class="cf-acc__val"></span><span class="cf-acc__edit"></span><svg class="cf-acc__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        head.querySelector('.cf-acc__q').textContent = t.textContent.replace(/\*\s*$/, '').trim();
        head.querySelector('.cf-acc__edit').textContent = editTxt;
        t.classList.add('cf-visually-hidden');
        if (t.tagName === 'LEGEND') t.after(head); else q.insertBefore(head, t.nextSibling);
        head.after(body);
        head.addEventListener('click', () => { if (q.classList.contains('is-open')) api.close(q); else api.open(q, true); });
        q.addEventListener('keydown', (e) => { if (/^Arrow/.test(e.key) && e.target.type === 'radio') q._kbd = true; });
        q.addEventListener('focusout', (e) => {
          if (q._kbd && !q.contains(e.relatedTarget) && valueOf(q)) { q._kbd = false; api.close(q); }
        });
        api.sync(q);
      },
      sync(q) {
        const v = valueOf(q);
        q.classList.toggle('is-answered', !!v);
        const val = q.querySelector('.cf-acc__val');
        if (val) val.textContent = v;
      },
      open(q, focus) {
        all().forEach((o) => { if (o !== q && o._acc) api.close(o); });
        q.classList.add('is-open');
        const h = q.querySelector('.cf-acc__head');
        if (h) h.setAttribute('aria-expanded', 'true');
        const b = q.querySelector('.cf-acc__body');
        if (b) b.hidden = false;
        if (focus) {
          const f = q.querySelector('input:checked, input, select, textarea');
          if (f) f.focus({ preventScroll: true });
          const r = q.getBoundingClientRect();
          if (r.top < 60 || r.top > window.innerHeight * 0.7) q.scrollIntoView({ block: 'center', behavior: motion() === 'off' ? 'auto' : 'smooth' });
        }
      },
      close(q) {
        if (!q._acc) return;
        api.sync(q);
        if (!q.classList.contains('is-answered') && q.querySelector('.has-error, [aria-invalid="true"]')) return;
        q.classList.remove('is-open');
        const h = q.querySelector('.cf-acc__head');
        if (h) h.setAttribute('aria-expanded', 'false');
        const b = q.querySelector('.cf-acc__body');
        if (b) b.hidden = true;
      },
      next(q) {
        const list = all().filter(visible);
        const i = list.indexOf(q);
        const n = list.slice(i + 1).find((o) => o._acc && !o.classList.contains('is-answered')) || null;
        if (n) api.open(n, false);
        return n;
      },
      refresh() {
        all().forEach((q) => { api.setup(q); if (q._acc) api.sync(q); });
        const list = all().filter((q) => visible(q) && q._acc);
        list.forEach((q) => { if (q.classList.contains('is-open') && q.classList.contains('is-answered') && !q.contains(document.activeElement)) api.close(q); });
        if (!list.some((q) => q.classList.contains('is-open'))) {
          const first = list.find((q) => !q.classList.contains('is-answered'));
          list.forEach((q) => api.close(q));
          if (first) api.open(first, false);
        }
      },
    };
    root.addEventListener('change', (e) => {
      const q = e.target.closest(sel);
      if (!q || !q._acc) return;
      api.sync(q);
      const single = e.target.type === 'radio' || e.target.tagName === 'SELECT';
      if (single && !q._kbd && valueOf(q)) {
        setTimeout(() => { if (!q.classList.contains('is-open')) return; api.close(q); api.next(q); }, 160); // laisse voir la sélection, puis replie (sauf si le visiteur a déjà ouvert une autre question)
      }
    });
    root._cfAcc = api;
    api.refresh();
    return api;
  }

  /* ---------- pied de page : accordéons sur mobile (ouverts sur ordinateur et sans JavaScript) ---------- */
  const footMq = window.matchMedia('(max-width: 749px)');
  function initFooterGroups(scope = document) {
    const groups = [...scope.querySelectorAll('.cf-footer__group')];
    const apply = () => groups.forEach((g) => {
      const sum = g.querySelector('summary');
      if (footMq.matches) { if (!g.hasAttribute('data-touched')) g.open = false; if (sum) sum.removeAttribute('tabindex'); }
      else { g.open = true; if (sum) sum.setAttribute('tabindex', '-1'); }
    });
    groups.forEach((g) => {
      if (!firstBind(g)) return;
      const sum = g.querySelector('summary');
      if (sum) sum.addEventListener('click', (e) => { if (!footMq.matches) e.preventDefault(); else g.setAttribute('data-touched', ''); });
    });
    apply();
    if (!footMq.cfBound) { footMq.cfBound = true; footMq.addEventListener('change', () => initFooterGroups()); }
  }

  /* ---------- bouton de chat Shopify Inbox (<shopify-chat>, prévu par Horizon) ----------
     Il occupe le coin inférieur droit : le bouton de devis mobile passe à gauche, et le chat
     se range pendant la saisie du formulaire de devis sur mobile (sauf conversation ouverte). */
  function initChatFab() {
    const chat = document.querySelector('shopify-chat');
    const root = document.documentElement;
    if (!chat || root.hasAttribute('data-cf-chat')) return;
    root.setAttribute('data-cf-chat', '');
    const mark = () => root.classList.add('cf-has-chat');
    if (customElements.get('shopify-chat')) mark(); else customElements.whenDefined('shopify-chat').then(mark);
    let inForm = false;
    const sync = () => {
      const a = document.activeElement;
      const typing = !!(a && a.matches && a.matches('.cf-quote input, .cf-quote textarea, .cf-quote select'));
      root.classList.toggle('cf-chat-tucked', footMq.matches && (inForm || typing));
    };
    const io = new IntersectionObserver((entries) => { inForm = entries.some((en) => en.isIntersecting); sync(); }, { threshold: 0.15 });
    document.querySelectorAll('.cf-quote form').forEach((f) => io.observe(f));
    document.addEventListener('focusin', sync);
    document.addEventListener('focusout', () => setTimeout(sync, 0));
    footMq.addEventListener('change', sync);
  }

  /* ---------- initialisation + éditeur de thème ---------- */
  /* bandeau défilant : duplique la liste (copie masquée aux technologies d'assistance) */
  function initMarquees(scope = document) {
    scope.querySelectorAll('.cf-marquee__track:not(.is-doubled)').forEach((t) => {
      const list = t.querySelector('.cf-marquee__list');
      if (!list) return;
      const copy = list.cloneNode(true);
      copy.removeAttribute('aria-labelledby');
      copy.setAttribute('aria-hidden', 'true');
      t.appendChild(copy);
      t.classList.add('is-doubled');
    });
  }

  /* liens vers une ancre de l'accueil : si la section est aussi sur cette page, lien local (pas de rechargement) */
  function localizeAnchors(scope = document) {
    const home = config.rootUrl || '/';
    scope.querySelectorAll('a[href*="#"]').forEach((a) => {
      const href = a.getAttribute('href');
      const i = href.indexOf('#');
      const base = href.slice(0, i);
      const id = href.slice(i + 1);
      if (!base || !id || (base !== home && base !== home + '/')) return;
      try { if (document.getElementById(decodeURIComponent(id))) a.setAttribute('href', '#' + id); } catch (e) { /* noop */ }
    });
  }

  function init(scope) {
    initReveal(scope); initCounters(scope); initMarquees(scope); localizeAnchors(document); syncMotionToggles();
    initFooterGroups(document); initChatFab(); watchHdrFrames(document);
    document.querySelectorAll('cf-mobile-cta').forEach((m) => { if (m.io) m.watch(); }); // nouveaux boutons de devis (éditeur)
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  /* Shopify échappe en HTML les traductions ({{ 'x' | t }}). Dans un bloc JSON lu par le script,
     « Automatisation & IA » arriverait donc sous la forme « Automatisation &amp; IA » : on décode. */
  const ENT = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&#x27;': "'", '&#34;': '"' };
  const decode = (s) => String(s).replace(/&(?:amp|lt|gt|quot|#39|#x27|#34);/g, (m) => ENT[m]);
  const i18n = (el) => {
    if (!el) return null;
    try { return JSON.parse(el.textContent, (k, v) => (typeof v === 'string' ? decode(v) : v)); } catch (e) { return null; }
  };

  window.CF = { decode, i18n, config, live, firstBind, release, whenNear, clamp, smooth, lerp, seg, motion, scroller, scrollTop, loop, onScroll, observeVisible, track, quote, goToQuote, init, accordion };
  document.dispatchEvent(new CustomEvent('cf:ready'));
})();
